import { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard, faQrcode, faCircleCheck, faCircleXmark, faCopy, faClock, faSpinner,
  faUserGroup, faChartLine, faBell, faLock,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./PaymentsModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const STATUS_LABELS = {
  pending: "Aguardando pagamento",
  confirmed: "Confirmado",
  received: "Pago",
  failed: "Falhou",
  active: "Ativa",
  canceled: "Cancelada",
  expired: "Expirada",
};

const METHOD_LABELS = { pix: "Pix", credit_card: "Cartão de crédito" };

const PROFESSOR_BENEFITS = [
  {
    icon: faUserGroup,
    title: "Gerenciar alunos",
    description: "Cadastre e organize seus alunos na plataforma.",
  },
  {
    icon: faChartLine,
    title: "Acompanhar progresso",
    description: "Veja métricas de aderência e evolução de cada aluno.",
  },
  {
    icon: faBell,
    title: "Alertas de atenção",
    description: "Receba notificações de alunos com baixa adesão.",
  },
  {
    icon: faLock,
    title: "Planos personalizados",
    description: "Crie e edite treinos exclusivos para seus alunos.",
  },
];

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch (_) {
    return iso;
  }
}

function LastPaymentInfo({ method, date }) {
  if (!method || !date) return null;
  return (
    <p className={styles.lastPaymentInfo}>
      Último pagamento: {formatDate(date)} via {METHOD_LABELS[method] || method}
    </p>
  );
}

const FINAL_PAYMENT_STATUSES = ["confirmed", "received", "overdue", "refunded", "failed"];
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 45;

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || (typeof d === "string" ? d : JSON.stringify(d))).join(" - ");
  }
  return JSON.stringify(detail);
}

function checkDigit(digits, weights) {
  const total = digits.split("").reduce((sum, d, i) => sum + Number(d) * weights[i], 0);
  const rem = total % 11;
  return rem < 2 ? 0 : 11 - rem;
}

function isValidCpfCnpj(digits) {
  if (![11, 14].includes(digits.length)) return false;
  if (new Set(digits).size === 1) return false;
  if (digits.length === 11) {
    const d1 = checkDigit(digits.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    const d2 = checkDigit(digits.slice(0, 9) + d1, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    return digits.slice(-2) === `${d1}${d2}`;
  }
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = checkDigit(digits.slice(0, 12), w1);
  const d2 = checkDigit(digits.slice(0, 12) + d1, w2);
  return digits.slice(-2) === `${d1}${d2}`;
}

function formatMoney(value) {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function copyPixCodeToClipboard(payload) {
  if (navigator.clipboard) navigator.clipboard.writeText(payload).catch(() => {});
}

function PixQrBlock({ qrImage, qrPayload }) {
  return (
    <>
      {qrImage && (
        <img
          src={`data:image/png;base64,${qrImage}`}
          alt="QR Code Pix"
          className={styles.qrImage}
        />
      )}
      {qrPayload && (
        <div className={styles.pixCopyRow}>
          <code className={styles.pixCode}>{qrPayload}</code>
          <button type="button" className={styles.copyBtn} onClick={() => copyPixCodeToClipboard(qrPayload)}>
            <FontAwesomeIcon icon={faCopy} /> Copiar
          </button>
        </div>
      )}
    </>
  );
}

export default function ProfessorCheckoutModal({ open, onClose, currentUser, onPaymentConfirmed, planId, planName, planAmount }) {
  const [loading, setLoading] = useState(true);
  const [billingKind, setBillingKind] = useState("subscription"); // subscription | oneoff
  const [method, setMethod] = useState("pix");
  const [cpfCnpj, setCpfCnpj] = useState(currentUser?.cpf_cnpj || "");
  const [card, setCard] = useState({ holder_name: "", number: "", expiry_month: "", expiry_year: "", ccv: "" });
  const [holder, setHolder] = useState({ postal_code: "", address_number: "", mobile_phone: "" });

  const [subscription, setSubscription] = useState(null);
  const [oneOffAccess, setOneOffAccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [cyclePixData, setCyclePixData] = useState(null);

  const [simulating, setSimulating] = useState(false);
  const [simulateError, setSimulateError] = useState("");

  const [cancelingPayment, setCancelingPayment] = useState(false);
  const [cancelPaymentError, setCancelPaymentError] = useState("");
  const [confirmCancelPayment, setConfirmCancelPayment] = useState(false);
  const [confirmCancelSubscription, setConfirmCancelSubscription] = useState(false);

  const [justConfirmed, setJustConfirmed] = useState(false);

  const pollRef = useRef(null);
  const cyclePollingRef = useRef(null);

  useBodyScrollLock(open);

  const needsCpf = !currentUser?.cpf_cnpj;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, pendingRes, accessRes] = await Promise.all([
        api.get("/payments/subscriptions/me").catch(() => ({ data: null })),
        api.get("/payments/pending").catch(() => ({ data: null })),
        api.get("/payments/access").catch(() => ({ data: null })),
      ]);
      if (subRes.data) {
        setSubscription(subRes.data);
      }
      if (accessRes.data) {
        setOneOffAccess(accessRes.data);
      }
      if (pendingRes.data) {
        setResult({ kind: "payment", data: pendingRes.data, pix: pendingRes.data.pix });
        setLiveStatus(pendingRes.data.status);
        if (!FINAL_PAYMENT_STATUSES.includes(pendingRes.data.status)) {
          startPolling(pendingRes.data.id);
        }
      }
    } catch (err) {
      // Sem assinatura ou pagamento pendente é ok
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!open) {
      setResult(null);
      setLiveStatus(null);
      setCyclePixData(null);
      setSubmitError("");
      setSimulateError("");
      setConfirmCancelPayment(false);
      setConfirmCancelSubscription(false);
      setCancelPaymentError("");
      setJustConfirmed(false);
      setOneOffAccess(null);
      clearInterval(pollRef.current);
      clearInterval(cyclePollingRef.current);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(cyclePollingRef.current);
  }, []);

  function startPolling(paymentId) {
    let attempts = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/${paymentId}/status`);
        console.log(`[Polling] Attempt ${attempts}: Payment ${paymentId} status = ${data.status}`);
        setLiveStatus(data.status);
        if (["received", "confirmed"].includes(data.status)) {
          console.log("[Polling] Payment confirmed! Calling onPaymentConfirmed");
          clearInterval(pollRef.current);
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          return;
        }
        if (FINAL_PAYMENT_STATUSES.includes(data.status) || attempts >= POLL_MAX_ATTEMPTS) {
          clearInterval(pollRef.current);
        }
      } catch (_) {
        if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(pollRef.current);
      }
    }, POLL_INTERVAL_MS);
  }

  function startCyclePolling() {
    let attempts = 0;
    clearInterval(cyclePollingRef.current);
    cyclePollingRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get("/payments/subscriptions/me");
        if (data?.pending_cycle_payment?.status && !["pending"].includes(data.pending_cycle_payment.status)) {
          if (["received", "confirmed"].includes(data.pending_cycle_payment.status)) {
            clearInterval(cyclePollingRef.current);
            setJustConfirmed(true);
            onPaymentConfirmed?.();
          }
          setSubscription(data);
        }
        if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(cyclePollingRef.current);
      } catch (_) {
        if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(cyclePollingRef.current);
      }
    }, POLL_INTERVAL_MS);
  }

  async function handleCancelSubscription(subscriptionId) {
    setCancelingPayment(true);
    setCancelPaymentError("");
    try {
      await api.delete(`/payments/subscriptions/${subscriptionId}`);
      clearInterval(cyclePollingRef.current);
      // Refetch billing access pra atualizar hasActivePlatformSubscription
      // (subscription foi cancelada, mesmo que ainda válida até o fim do período).
      await onPaymentConfirmed?.();
      // Refetch a subscription pra mostrar o status "canceled" neste modal
      const { data: updatedSub } = await api.get("/payments/subscriptions/me");
      setSubscription(updatedSub);
    } catch (err) {
      setCancelPaymentError(extractError(err));
    } finally {
      setCancelingPayment(false);
    }
  }

  async function handleSimulateConfirm(paymentId) {
    setSimulating(true);
    setSimulateError("");
    try {
      await api.post(`/payments/${paymentId}/simulate-confirm`);
      clearInterval(cyclePollingRef.current);
      clearInterval(pollRef.current);
      setJustConfirmed(true);
      onPaymentConfirmed?.();
      load();
    } catch (err) {
      setSimulateError(err.response?.data?.detail || err.message);
    } finally {
      setSimulating(false);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    if (!planId) { setSubmitError("Plano não especificado."); return; }
    if (needsCpf) {
      if (!cpfCnpj.trim()) { setSubmitError("Informe seu CPF/CNPJ."); return; }
      if (![11, 14].includes(cpfCnpj.trim().length)) {
        setSubmitError("CPF deve ter 11 dígitos ou CNPJ 14 dígitos.");
        return;
      }
      if (!isValidCpfCnpj(cpfCnpj.trim())) {
        setSubmitError("CPF/CNPJ inválido.");
        return;
      }
    }

    const payload = { plan_id: planId };
    if (needsCpf) payload.cpf_cnpj = cpfCnpj.trim();

    if (method === "credit_card") {
      if (!card.holder_name || !card.number || !card.expiry_month || !card.expiry_year || !card.ccv) {
        setSubmitError("Preencha todos os dados do cartão.");
        return;
      }
      if (card.number.length < 13 || card.number.length > 19) {
        setSubmitError("Número do cartão inválido.");
        return;
      }
      if (!/^(0[1-9]|1[0-2])$/.test(card.expiry_month)) {
        setSubmitError("Mês de validade inválido (MM, de 01 a 12).");
        return;
      }
      if (!/^\d{4}$/.test(card.expiry_year)) {
        setSubmitError("Ano de validade inválido (AAAA, 4 dígitos).");
        return;
      }
      if (card.ccv.length < 3 || card.ccv.length > 4) {
        setSubmitError("CVV inválido.");
        return;
      }
      if (!holder.postal_code || !holder.address_number) {
        setSubmitError("Informe CEP e número do endereço de cobrança.");
        return;
      }
      if (holder.postal_code.length < 8 || holder.postal_code.length > 9) {
        setSubmitError("CEP inválido.");
        return;
      }
      if (!holder.mobile_phone) {
        setSubmitError("Informe o telefone/celular do titular do cartão.");
        return;
      }
      payload.credit_card = { ...card };
      payload.credit_card_holder_info = {
        name: card.holder_name,
        email: currentUser?.email,
        cpf_cnpj: needsCpf ? cpfCnpj.trim() : currentUser?.cpf_cnpj,
        postal_code: holder.postal_code,
        address_number: holder.address_number,
        mobile_phone: holder.mobile_phone || undefined,
      };
    }

    setSubmitting(true);
    try {
      if (billingKind === "subscription") {
        const { data } = await api.post("/payments/subscriptions", {
          ...payload,
          billing_type: method === "pix" ? "PIX" : "CREDIT_CARD",
        });
        setResult({ kind: "subscription", data });
        setSubscription(data);
        if (method === "pix") {
          if (data?.pending_cycle_payment?.pix?.qr_code_payload) {
            setCyclePixData(data.pending_cycle_payment.pix);
          }
          startCyclePolling();
        } else if (data.last_payment) {
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          load();
        }
      } else {
        const { data } = await api.post("/payments/checkout", { ...payload, method });
        setResult({ kind: "payment", data, pix: data.pix });
        setLiveStatus(data.status);
        if (method === "pix" && data.id && !FINAL_PAYMENT_STATUSES.includes(data.status)) {
          startPolling(data.id);
        } else if (["received", "confirmed"].includes(data.status)) {
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          load();
        }
      }
    } catch (err) {
      setSubmitError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const effectiveStatus = liveStatus || result?.data?.status;

  const headerTitle = "Pagamentos";
  const headerSub = "Gerencie sua assinatura ou faça um pagamento avulso";

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} disabled={submitting || cancelingPayment} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faCreditCard} /></span>
            <div>
              <h2 className={styles.title}>{headerTitle}</h2>
              <p className={styles.sub}>{headerSub}</p>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loadingMsg}>Carregando…</div>
          ) : justConfirmed ? (
            <div className={styles.confirmedView}>
              <div className={styles.confirmedIcon}>
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <h3 className={styles.confirmedTitle}>{result?.kind === "subscription" ? "Assinatura confirmada!" : "Pagamento confirmado!"}</h3>
              <p className={styles.confirmedText}>
                {result?.kind === "subscription" 
                  ? `Seu plano "${planName}" já está ativo. Aproveite os benefícios:`
                  : "Seu pagamento foi confirmado com sucesso. Você tem acesso total à plataforma!"}
              </p>
              {result?.kind === "subscription" && (
                <div className={styles.benefitsList}>
                  {PROFESSOR_BENEFITS.map(b => (
                    <div key={b.title} className={styles.benefitItem}>
                      <span className={styles.benefitIcon}><FontAwesomeIcon icon={b.icon} /></span>
                      <div>
                        <strong>{b.title}</strong>
                        <p>{b.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={styles.btnPrimary}
                style={{ width: "100%" }}
                onClick={() => { setJustConfirmed(false); onClose(); }}
              >
                Começar a usar
              </button>
            </div>
          ) : subscription ? (
            <div className={styles.subStatus}>
              <div className={`${styles.subBadge} ${subscription.status === "canceled" ? styles.subBadgeCanceled : ""}`}>
                {STATUS_LABELS[subscription.status] || subscription.status}
              </div>
              <h3 className={styles.subPlanName}>{subscription.plan_name}</h3>
              <p className={styles.subAmount}>{formatMoney(subscription.plan_amount)}/mês</p>
              {subscription.status === "canceled" ? (
                <p className={styles.hint}>
                  Sua assinatura foi cancelada e não vai renovar, mas seu acesso continua válido até{" "}
                  {formatDate(subscription.current_period_end)}.
                </p>
              ) : (
                <div className={styles.subMeta}>
                  <span>Próxima cobrança: {formatDate(subscription.current_period_end)}</span>
                </div>
              )}
              <LastPaymentInfo method={subscription.last_payment?.method} date={subscription.last_payment?.date} />

              {subscription.pending_cycle_payment && (
                <div className={styles.cyclePaymentBox}>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={faClock} /> Cobrança deste ciclo pendente - {formatMoney(subscription.pending_cycle_payment.amount)}
                  </div>
                  <PixQrBlock
                    qrImage={subscription.pending_cycle_payment.pix?.qr_code_image}
                    qrPayload={subscription.pending_cycle_payment.pix?.qr_code_payload}
                  />
                  <p className={styles.hint}>
                    Pix não tem débito automático - pague esse QR pra manter sua assinatura em dia.
                  </p>
                  <button type="button" className={styles.refreshLink} onClick={load} disabled={loading}>
                    {loading ? "Atualizando…" : "Atualizar status"}
                  </button>
                  {process.env.NODE_ENV === "development" && (
                    <>
                      <button
                        type="button"
                        className={styles.devSimulateBtn}
                        onClick={() => handleSimulateConfirm(subscription.pending_cycle_payment.id)}
                        disabled={simulating}
                      >
                        {simulating ? "Simulando…" : "[DEV] Simular pagamento confirmado"}
                      </button>
                      {simulateError && <div className={styles.error}>{simulateError}</div>}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : result ? (
            <div className={styles.resultView}>
              {result.kind === "subscription" && cyclePixData ? (
                <>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Aguardando pagamento
                  </div>
                  <PixQrBlock qrImage={cyclePixData.qr_code_image} qrPayload={cyclePixData.qr_code_payload} />
                  <p className={styles.hint}>
                    Pague com o app do seu banco. O status atualiza sozinho aqui assim que a confirmação chegar.
                  </p>
                </>
              ) : result.kind === "subscription" ? (
                <>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Cartão em análise
                  </div>
                  <p className={styles.hint}>
                    Seu cartão está em análise pela Asaas. Isso é raro e costuma ser rápido - o
                    status atualiza sozinho aqui assim que a confirmação chegar.
                  </p>
                </>
              ) : result.pix ? (
                <>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={
                      effectiveStatus === "pending" ? faSpinner :
                      effectiveStatus === "failed" ? faCircleXmark :
                      (effectiveStatus === "confirmed" || effectiveStatus === "received") ? faCircleCheck :
                      faClock
                    } spin={effectiveStatus === "pending"} />
                    {STATUS_LABELS[effectiveStatus] || effectiveStatus}
                  </div>
                  <PixQrBlock qrImage={result.pix.qr_code_image} qrPayload={result.pix.qr_code_payload} />
                  <p className={styles.hint}>
                    Pague com o app do seu banco. O status atualiza sozinho aqui assim que a confirmação chegar.
                  </p>
                  {effectiveStatus === "pending" && (
                    confirmCancelPayment ? (
                      <div className={styles.cancelRow}>
                        <span>Cancelar esse pagamento e começar outro?</span>
                        <button type="button" className={styles.btnSecondary}
                          onClick={() => setConfirmCancelPayment(false)} disabled={cancelingPayment}>
                          Voltar
                        </button>
                        <button type="button" className={styles.btnDanger}
                          onClick={() => handleCancelSubscription(result.data.id)} disabled={cancelingPayment}>
                          {cancelingPayment ? "Cancelando…" : "Confirmar cancelamento"}
                        </button>
                      </div>
                    ) : (
                      <button type="button" className={styles.linkBtn} onClick={() => setConfirmCancelPayment(true)}>
                        Cancelar este pagamento
                      </button>
                    )
                  )}
                  {cancelPaymentError && <div className={styles.error}>{cancelPaymentError}</div>}
                  {process.env.NODE_ENV === "development" && !FINAL_PAYMENT_STATUSES.includes(effectiveStatus) && (
                    <>
                      <button
                        type="button"
                        className={styles.devSimulateBtn}
                        onClick={() => handleSimulateConfirm(result.data.id)}
                        disabled={simulating}
                      >
                        {simulating ? "Simulando…" : "[DEV] Simular pagamento confirmado"}
                      </button>
                      {simulateError && <div className={styles.error}>{simulateError}</div>}
                    </>
                  )}
                </>
              ) : null}
              <button className={styles.btnPrimary} onClick={onClose} style={{ width: "100%" }}>
                Fechar
              </button>
            </div>
          ) : oneOffAccess && oneOffAccess.expires_at && new Date(oneOffAccess.expires_at) > new Date() ? (
            <div className={styles.resultView}>
              <div className={styles.statusBadge} style={{ background: "rgba(29, 158, 117, 0.2)", border: "1px solid rgba(29, 158, 117, 0.4)", color: "var(--accent)", padding: "6px 12px", borderRadius: "6px", display: "inline-block", marginBottom: "16px", fontSize: "12px", fontWeight: "600", letterSpacing: "0.5px" }}>
                ACESSO ATIVO
              </div>
              <h3 className={styles.subPlanName} style={{ marginTop: "16px" }}>Pagamento avulso</h3>
              <p className={styles.subAmount} style={{ margin: "8px 0" }}>
                {oneOffAccess?.amount ? formatMoney(oneOffAccess.amount) : "-"}
              </p>
              <p className={styles.hint} style={{ margin: "12px 0" }}>
                Válido até {new Date(oneOffAccess.expires_at).toLocaleDateString("pt-BR")}
              </p>
              <p className={styles.hint}>
                Não é possível assinar ou pagar avulso de novo antes dessa data. Último pagamento: {oneOffAccess.updated_at ? new Date(oneOffAccess.updated_at).toLocaleDateString("pt-BR") : "-"} via {oneOffAccess.method === "pix" ? "Pix" : "Cartão"}
              </p>
            </div>
          ) : (
            <div className={styles.form}>
              <div className={styles.toggleRow}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${billingKind === "subscription" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setBillingKind("subscription")}
                >
                  Assinatura
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${billingKind === "oneoff" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setBillingKind("oneoff")}
                >
                  Pagar Avulso
                </button>
              </div>

              <div className={styles.toggleRow} style={{ marginTop: "16px" }}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${method === "pix" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setMethod("pix")}
                >
                  <FontAwesomeIcon icon={faQrcode} /> Pix
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${method === "credit_card" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setMethod("credit_card")}
                >
                  <FontAwesomeIcon icon={faCreditCard} /> Cartão
                </button>
              </div>

              {needsCpf && (
                <label className={styles.field}>
                  CPF/CNPJ
                  <input
                    value={cpfCnpj}
                    inputMode="numeric"
                    onChange={e => setCpfCnpj(e.target.value.replace(/\D/g, ""))}
                    placeholder="Somente números"
                  />
                </label>
              )}
              {!needsCpf && currentUser?.cpf_cnpj && (
                <label className={styles.field}>
                  CPF/CNPJ
                  <input
                    value={currentUser.cpf_cnpj}
                    disabled
                    title="Salvo em seu perfil"
                  />
                </label>
              )}

              {method === "credit_card" && (
                <div className={styles.cardFields}>
                  <label className={styles.field}>
                    Nome impresso no cartão
                    <input value={card.holder_name}
                      onChange={e => setCard(c => ({ ...c, holder_name: e.target.value }))} />
                  </label>
                  <label className={styles.field}>
                    Número do cartão
                    <input value={card.number} inputMode="numeric"
                      onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/\D/g, "") }))} />
                  </label>
                  <div className={styles.row3}>
                    <label className={styles.field}>
                      Mês (MM)
                      <input value={card.expiry_month} maxLength={2} inputMode="numeric"
                        onChange={e => setCard(c => ({ ...c, expiry_month: e.target.value.replace(/\D/g, "") }))} />
                    </label>
                    <label className={styles.field}>
                      Ano (AAAA)
                      <input value={card.expiry_year} maxLength={4} inputMode="numeric"
                        onChange={e => setCard(c => ({ ...c, expiry_year: e.target.value.replace(/\D/g, "") }))} />
                    </label>
                    <label className={styles.field}>
                      CVV
                      <input value={card.ccv} maxLength={4} inputMode="numeric"
                        onChange={e => setCard(c => ({ ...c, ccv: e.target.value.replace(/\D/g, "") }))} />
                    </label>
                  </div>
                  <div className={styles.row2}>
                    <label className={styles.field}>
                      CEP
                      <input value={holder.postal_code} inputMode="numeric"
                        onChange={e => setHolder(h => ({ ...h, postal_code: e.target.value.replace(/\D/g, "") }))} />
                    </label>
                    <label className={styles.field}>
                      Número do endereço
                      <input value={holder.address_number}
                        onChange={e => setHolder(h => ({ ...h, address_number: e.target.value }))} />
                    </label>
                  </div>
                  <label className={styles.field}>
                    Telefone/celular (com DDD)
                    <input value={holder.mobile_phone} inputMode="numeric" placeholder="Ex: 11999998888"
                      onChange={e => setHolder(h => ({ ...h, mobile_phone: e.target.value.replace(/\D/g, "") }))} />
                  </label>
                </div>
              )}

              {submitting && (
                <div className={styles.hint} role="status">
                  <FontAwesomeIcon icon={faSpinner} spin />{" "}
                  {method === "credit_card"
                    ? "Aguardando confirmação do cartão na Asaas… isso pode levar alguns segundos, não feche esta tela."
                    : "Gerando cobrança na Asaas…"}
                </div>
              )}
              {submitError && <div className={styles.error}>{submitError}</div>}
            </div>
          )}
        </div>

        {subscription && subscription.status === "active" && (
          <div className={styles.footer}>
            {!confirmCancelSubscription ? (
              <button className={styles.btnDanger} onClick={() => setConfirmCancelSubscription(true)}>
                Cancelar assinatura
              </button>
            ) : (
              <div className={styles.confirmBox}>
                <p>Cancelar sua assinatura? Você perde o acesso ao fim do período atual.</p>
                {cancelPaymentError && <div className={styles.error}>{cancelPaymentError}</div>}
                <div className={styles.confirmActions}>
                  <button className={styles.btnSecondary} onClick={() => setConfirmCancelSubscription(false)} disabled={cancelingPayment}>
                    Voltar
                  </button>
                  <button className={styles.btnDanger} onClick={() => handleCancelSubscription(subscription.id)} disabled={cancelingPayment}>
                    {cancelingPayment ? "Cancelando…" : "Confirmar cancelamento"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !result && !justConfirmed && !subscription && !(oneOffAccess && oneOffAccess.expires_at && new Date(oneOffAccess.expires_at) > new Date()) && (
          <div className={styles.footer}>
            <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button type="button" className={styles.btnPrimary} onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Processando…</>
              ) : billingKind === "subscription" ? (
                "Assinar " + formatMoney(planAmount)
              ) : (
                "Pagar " + formatMoney(planAmount)
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
